import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send } from 'lucide-react';
import { MATERIAL_TYPES, TRUCK_TYPES, WHATSAPP_LINK } from '../utils/constants';

const BookingForm = () => {
  const [formData, setFormData] = useState({
    name: '', pickup: '', pickupPostal: '', drop: '', dropPostal: '', vehicleType: '', truckSize: '', loadType: '', date: '', phone: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = () => {
    let newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.phone.match(/^[6789]\d{9}$/)) newErrors.phone = "Enter a valid 10-digit number";
    if (!formData.pickup.trim()) newErrors.pickup = "Pickup is required";
    if (!formData.drop.trim()) newErrors.drop = "Destination is required";
    if (!formData.vehicleType) newErrors.vehicleType = "Select a vehicle";
    if (formData.vehicleType === 'Eicher' && (!formData.truckSize || formData.truckSize === 'Select Size')) {
      newErrors.truckSize = "Select size";
    }
    if (!formData.loadType) newErrors.loadType = "Select load type";
    if (!formData.date) newErrors.date = "Select a date";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    if (name === 'vehicleType' && value !== 'Eicher') updated.truckSize = '';
    setFormData(updated);
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    const message = `*PKRouteX Booking Request*\n\n` +
      `*Name:* ${formData.name}\n` +
      `*Pickup:* ${formData.pickup} - ${formData.pickupPostal}\n` +
      `*Drop:* ${formData.drop} - ${formData.dropPostal}\n` +
      `*Vehicle Type:* ${formData.vehicleType}\n` +
      `*Truck Size:* ${formData.truckSize || 'N/A'}\n` +
      `*Load Type:* ${formData.loadType}\n` +
      `*Date:* ${formData.date}\n` +
      `*Phone:* ${formData.phone}`;
    window.open(WHATSAPP_LINK(message), '_blank');
    setIsSubmitting(false);
  };

  const inputClass = (name) => `w-full bg-slate-50 border ${errors[name] ? 'border-red-500' : 'border-black/10'} rounded-xl px-6 py-4 focus:border-brand-orange outline-none transition-all text-slate-950 placeholder-slate-400 font-medium`;
  const labelClass = "text-[10px] font-black text-brand-orange uppercase tracking-[0.2em] pl-1 mb-1.5 flex justify-between";

  return (
    <section id="book" className="section-padding bg-brand-bg relative">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 text-brand-black">READY TO <span className="text-brand-orange text-glow">SHIP?</span></h2>
          <p className="text-brand-muted">Fill the form below and our team will connect via WhatsApp instantly.</p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          className="bg-white p-4 sm:p-6 md:p-12 rounded-[1.5rem] sm:rounded-[2rem] border border-black/8 shadow-lg"
        >
          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-1">
              <label className={labelClass}>
                Name {errors.name && <span className="text-red-500 font-bold lowercase tracking-normal">({errors.name})</span>}
              </label>
              <input name="name" value={formData.name} onChange={handleChange} type="text" placeholder="John Doe" className={inputClass('name')} />
            </div>
            <div className="space-y-1">
              <label className={labelClass}>
                Phone Number {errors.phone && <span className="text-red-500 font-bold lowercase tracking-normal">({errors.phone})</span>}
              </label>
              <input name="phone" value={formData.phone} onChange={handleChange} type="tel" placeholder="9876543210" className={inputClass('phone')} />
            </div>
            <div className="space-y-1">
              <label className={labelClass}>
                Pickup Location {errors.pickup && <span className="text-red-500 font-bold lowercase tracking-normal">({errors.pickup})</span>}
              </label>
              <input name="pickup" value={formData.pickup} onChange={handleChange} type="text" placeholder="Origin City" className={inputClass('pickup')} />
            </div>
            <div className="space-y-1">
              <label className={labelClass}>Pickup Postal Code</label>
              <input name="pickupPostal" value={formData.pickupPostal} onChange={handleChange} type="text" placeholder="e.g. 600001" maxLength={6} className={inputClass('pickupPostal')} />
            </div>
            <div className="space-y-1">
              <label className={labelClass}>
                Drop Location {errors.drop && <span className="text-red-500 font-bold lowercase tracking-normal">({errors.drop})</span>}
              </label>
              <input name="drop" value={formData.drop} onChange={handleChange} type="text" placeholder="Destination City" className={inputClass('drop')} />
            </div>
            <div className="space-y-1">
              <label className={labelClass}>Drop Location Postal Code</label>
              <input name="dropPostal" value={formData.dropPostal} onChange={handleChange} type="text" placeholder="e.g. 110001" maxLength={6} className={inputClass('dropPostal')} />
            </div>
            <div className="space-y-1">
              <label className={labelClass}>
                Vehicle Type {errors.vehicleType && <span className="text-red-500 font-bold lowercase tracking-normal">({errors.vehicleType})</span>}
              </label>
              <select name="vehicleType" value={formData.vehicleType} onChange={handleChange} className={inputClass('vehicleType') + " appearance-none"}>
                <option value="">Select Vehicle</option>
                {TRUCK_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>

            {formData.vehicleType === 'Eicher' && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="space-y-1">
                <label className={labelClass}>
                  Truck Size {errors.truckSize && <span className="text-red-500 font-bold lowercase tracking-normal">({errors.truckSize})</span>}
                </label>
                <select name="truckSize" onChange={handleChange} value={formData.truckSize} className={inputClass('truckSize') + " border-brand-orange/40 appearance-none"}>
                  <option value="Select Size">Select Size</option>
                  <option value="14 ft">14 ft</option>
                  <option value="17 ft">17 ft</option>
                  <option value="19 ft">19 ft</option>
                  <option value="20 ft">20 ft</option>
                  <option value="22 ft">22 ft</option>
                  <option value="24 ft">24 ft</option>
                  <option value="32 ft">32 ft</option>
                </select>
              </motion.div>
            )}

            <div className="space-y-1">
              <label className={labelClass}>
                Load Type {errors.loadType && <span className="text-red-500 font-bold lowercase tracking-normal">({errors.loadType})</span>}
              </label>
              <select name="loadType" value={formData.loadType} onChange={handleChange} className={inputClass('loadType') + " appearance-none"}>
                <option value="">Select Load Type</option>
                {MATERIAL_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
            <div className="md:col-span-2 sm:col-span-2 space-y-1">
              <label className={labelClass}>
                Preferred Date {errors.date && <span className="text-red-500 font-bold lowercase tracking-normal">({errors.date})</span>}
              </label>
              <input name="date" value={formData.date} onChange={handleChange} type="date" className={inputClass('date')} />
            </div>

            <motion.button
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`sm:col-span-2 mt-3 sm:mt-4 py-4 sm:py-5 rounded-2xl text-base sm:text-lg font-black tracking-widest flex items-center justify-center gap-3 transition-all ${isSubmitting ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-brand-orange text-white orange-glow'}`}
            >
              {isSubmitting ? 'PROCESSING...' : 'GENERATE BOOKING'} <Send size={20} className={isSubmitting ? 'animate-pulse' : ''} />
            </motion.button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default BookingForm;
